import {
  AuthDesktopCard,
  AuthDesktopHeader,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenFooter,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { getApiErrorMessage } from "@/lib/api";
import authService from "@/lib/auth";
import { showToast } from "@/lib/toast";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText } from "react-native-paper";
import { z } from "zod";

const RESEND_COOLDOWN_SECONDS = 60;

const verifyEmailSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code from your email"),
});

type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;

export default function VerifyEmailScreen() {
  const { user, setUser, signOut } = useAuth();
  const desktop = useDesktopLayout();
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [isResending, setIsResending] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyEmailFormData>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: {
      code: "",
    },
  });

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setResendSecondsLeft((seconds) => seconds - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [resendSecondsLeft]);

  const onSubmit = async (data: VerifyEmailFormData) => {
    try {
      const verifiedUser = await authService.verifyEmail(data.code);
      setUser(verifiedUser);
      showToast.success("Email verified");

      if (verifiedUser.role === "driver") {
        const needsOnboarding =
          !verifiedUser.stripe_account?.details_submitted ||
          !verifiedUser.driver?.is_verified ||
          (verifiedUser.vehicles_count || 0) === 0;
        if (needsOnboarding) {
          router.replace("/complete-registration");
        }
      }
    } catch (error) {
      showToast.error(getApiErrorMessage(error, "Verification failed"));
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await authService.sendVerificationCode();
      showToast.success("A new code has been sent to your email");
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      showToast.error(getApiErrorMessage(error, "Failed to send code"));
    } finally {
      setIsResending(false);
    }
  };

  const canResend = resendSecondsLeft <= 0 && !isResending;

  const title = "Verify your email";
  const subtitle = `Enter the 6-digit code we sent to ${user?.email ?? "your email"}`;

  const formFields = (
    <View
      style={[
        desktop ? authScreenStyles.desktopForm : authScreenStyles.form,
        desktop && styles.desktopForm,
      ]}
    >
      <View style={authScreenStyles.field}>
        <Label label="Verification code" required={false} />
        <Controller
          control={control}
          name="code"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="123456"
              value={value}
              onChangeText={(text) =>
                onChange(text.replace(/\D/g, "").slice(0, 6))
              }
              onBlur={onBlur}
              keyboardType="number-pad"
              autoComplete="one-time-code"
              textContentType="oneTimeCode"
              maxLength={6}
              error={!!errors.code}
              style={[
                authScreenStyles.input,
                desktop && authScreenStyles.desktopInput,
              ]}
              outlineStyle={
                desktop
                  ? authScreenStyles.desktopInputOutline
                  : authScreenStyles.inputOutline
              }
            />
          )}
        />
        {errors.code && (
          <HelperText type="error" visible={!!errors.code}>
            {errors.code?.message}
          </HelperText>
        )}
      </View>

      <TouchableOpacity onPress={handleResend} disabled={!canResend}>
        <ThemedText
          style={[authScreenStyles.link, !canResend && { opacity: 0.5 }]}
          type="smallSemiBold"
        >
          {resendSecondsLeft > 0
            ? `Resend code in ${resendSecondsLeft}s`
            : isResending
              ? "Sending..."
              : "Resend code"}
        </ThemedText>
      </TouchableOpacity>
    </View>
  );

  const submitButton = (
    <BrandButton
      variant="primary"
      loading={isSubmitting}
      onPress={handleSubmit(onSubmit)}
      style={
        desktop
          ? authScreenStyles.desktopContinueButton
          : authScreenStyles.continueButton
      }
      labelStyle={
        desktop
          ? authScreenStyles.desktopContinueButtonLabel
          : authScreenStyles.continueButtonLabel
      }
      contentStyle={authScreenStyles.continueButtonContent}
      icon={() => (
        <Ionicons
          name="chevron-forward"
          size={desktop ? 14 : 18}
          color={BrandColors.white}
        />
      )}
    >
      {isSubmitting ? "Verifying..." : "Verify Email"}
    </BrandButton>
  );

  const signOutFooter = (
    <AuthScreenFooter>
      <ThemedText style={authScreenStyles.footerText} type="small">
        Wrong account?{" "}
      </ThemedText>
      <TouchableOpacity onPress={signOut}>
        <ThemedText style={authScreenStyles.link} type="smallSemiBold">
          Sign Out
        </ThemedText>
      </TouchableOpacity>
    </AuthScreenFooter>
  );

  if (desktop) {
    return (
      <AuthDesktopLayout onBack={undefined}>
        <AuthDesktopHeader title={title} subtitle={subtitle} />
        <AuthDesktopCard>
          {formFields}
          {submitButton}
          {signOutFooter}
        </AuthDesktopCard>
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={() => {}} showBackButton={false} keyboardInset>
      <AuthScreenContent>
        <AuthScreenHeader title={title} subtitle={subtitle} />

        {formFields}
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>{submitButton}</AuthScreenContent>

      {signOutFooter}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  desktopForm: {
    marginBottom: 20,
  },
});
